import { useEffect, useRef } from 'react';

/**
  * Keeps a list's hover state honest while it scrolls.
  *
  * Hover is driven by mouseenter, which only fires when the pointer itself
  * moves. Scrolling slides a different row under a stationary cursor without
  * firing anything, leaving the previous row lit. On each scroll this re-resolves
  * whatever now sits under the pointer and reports it.
  *
  * Rows opt in by carrying a `data-hover-id` attribute.
  */
export default function useHoverOnScroll(scrollRef, onHover) {
    const pos = useRef(null);
    const cb = useRef(onHover);
    cb.current = onHover;

    useEffect(() => {
        const onMove = (e) => {
            pos.current = { x: e.clientX, y: e.clientY };
        };
        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        let raf = 0;
        const resolve = () => {
            raf = 0;
            const p = pos.current;
            if (!p) return; // pointer hasn't moved yet this session
            const under = document.elementFromPoint(p.x, p.y);
            const row = under && under.closest('[data-hover-id]');
            cb.current(row ? row.getAttribute('data-hover-id') : null);
        };
        // Scroll fires far faster than paint; one resolve per frame is plenty.
        const onScroll = () => {
            if (!raf) raf = requestAnimationFrame(resolve);
        };

        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            el.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(raf);
        };
    }, [scrollRef]);
}
